import { useCallback, useMemo, useRef, useState } from 'react';
import type { ZodType } from 'zod';

/**
 * Controlled form state with schema validation.
 *
 * Deliberately not react-hook-form: every form in this console is a flat object
 * of scalars validated once on submit, which is the case RHF's uncontrolled
 * machinery buys nothing for. This is the whole contract in fifty lines, and it
 * keeps the dependency list honest.
 *
 * Validation runs on submit rather than on change. Marking a field invalid
 * while somebody is still typing it is noise -- `budtender@` is not a mistake,
 * it is an unfinished address -- so errors appear when the answer is claimed to
 * be complete, and clear as soon as the field is touched again.
 */
export interface Form<T> {
  values: T;
  errors: Partial<Record<keyof T, string>>;
  /** True once any field differs from the values the form was opened with. */
  isDirty: boolean;
  /**
   * Accepts a value or an updater. The updater form matters whenever two
   * changes to the same field can land in one tick — toggling several tags
   * before React re-renders, say — because a value computed from the rendered
   * props would be computed from the state before the first toggle.
   */
  set: <K extends keyof T>(key: K, value: T[K] | ((previous: T[K]) => T[K])) => void;
  /** Replace every value and clear errors, e.g. when a fresh record loads. */
  reset: (next?: T) => void;
  /** Validates; calls `onValid` with the parsed output only if it passes. */
  submit: (onValid: (parsed: T) => void) => (event?: { preventDefault(): void }) => void;
}

export function useForm<T extends Record<string, unknown>>(
  initial: T,
  schema: ZodType<T>,
): Form<T> {
  const [values, setValues] = useState<T>(initial);
  const [errors, setErrors] = useState<Partial<Record<keyof T, string>>>({});

  /*
   * The baseline for `isDirty`, held in a ref so that re-rendering with a new
   * `initial` object -- which happens on every render at most call sites --
   * does not silently redefine what "unchanged" means.
   */
  const baseline = useRef(initial);

  const set = useCallback(<K extends keyof T>(key: K, value: T[K] | ((previous: T[K]) => T[K])) => {
    setValues((current) => ({
      ...current,
      [key]:
        typeof value === 'function' ? (value as (previous: T[K]) => T[K])(current[key]) : value,
    }));
    setErrors((current) => ({ ...current, [key]: undefined }));
  }, []);

  const reset = useCallback((next?: T) => {
    if (next) baseline.current = next;

    setValues(baseline.current);
    setErrors({});
  }, []);

  const submit = useCallback(
    (onValid: (parsed: T) => void) => (event?: { preventDefault(): void }) => {
      event?.preventDefault();

      const parsed = schema.safeParse(values);

      if (!parsed.success) {
        const next: Partial<Record<keyof T, string>> = {};

        // First message per field: a list of three complaints about one input
        // is not three times as useful as the first one.
        for (const issue of parsed.error.issues) {
          const field = issue.path[0] as keyof T;
          next[field] ??= issue.message;
        }

        setErrors(next);
        return;
      }

      onValid(parsed.data);
    },
    [schema, values],
  );

  const isDirty = useMemo(
    () => JSON.stringify(values) !== JSON.stringify(baseline.current),
    [values],
  );

  return { values, errors, isDirty, set, reset, submit };
}

/**
 * A URL-safe slug from a display name.
 *
 * Every catalog table has a `name` and a unique `(account_id, slug)`. Typing
 * the slug by hand is busywork that also invites collisions, so forms derive it
 * until somebody edits it themselves.
 */
export function slugify(value: string): string {
  return (
    value
      .toLowerCase()
      .trim()
      .normalize('NFD')
      // Strip combining marks so "Piña" becomes "pina" rather than "pia".
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 80)
  );
}
